import { http } from '@noor/api';
import {
  bonusOptionSchema,
  clientOptionSchema,
  foulOptionSchema,
  transactionListSchema,
  type BonusOption,
  type ClientOption,
  type FoulOption,
  type OptionsPage,
  type TransactionList,
} from '../model/schema';
import type { TransactionsListFilter, TransactionsReportPayload } from '../lib/filters';

/**
 * Transactions + report API — the exact cpanel paths, relative to the shared
 * `http` base (`…/api/v1`). Query objects drop empty values (the old `qs`
 * behavior); responses are parsed at the boundary.
 */

export type TransactionId = string | number;

/** Reports downloaded from the transactions page (the "Export" menu). */
export type TransactionReportKind =
  | 'transactions'
  | 'transactions_total'
  | 'battery'
  | 'orders'
  | 'orders_total';

/** Export endpoint per report (cpanel `transactions.endpoints` + `orderReports`). */
const REPORT_PATH: Record<TransactionReportKind, string> = {
  transactions: 'admin/transactions/export',
  transactions_total: 'admin/transactions/grouped-export',
  battery: 'admin/transactions/export-battery',
  orders: 'admin/orders/export',
  orders_total: 'admin/orders/grouped-export',
};

export interface OptionsFilter {
  limit: number;
  skip: number;
  search?: string | undefined;
  /** Fouls only: scope the templates to one courier (cpanel `courier_id`). */
  courier_id?: string | undefined;
}

function toQuery(filter: object): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      query[key] = String(value);
    }
  }
  return query;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Tolerant page parse: invalid rows are dropped, `count` falls back to the length. */
function parseOptionsPage<T>(
  raw: unknown,
  parse: (item: unknown) => { success: boolean; data?: T },
): OptionsPage<T> {
  const list: unknown[] =
    isRecord(raw) && Array.isArray(raw.data)
      ? (raw.data as unknown[])
      : Array.isArray(raw)
        ? (raw as unknown[])
        : [];
  const data: T[] = [];
  for (const item of list) {
    const parsed = parse(item);
    if (parsed.success && parsed.data !== undefined) data.push(parsed.data);
  }
  return {
    data,
    count: isRecord(raw) && typeof raw.count === 'number' ? raw.count : data.length,
  };
}

export const transactionApi = {
  /** `GET admin/transactions` — rows + the summary strip + the total count. */
  async list(filter: TransactionsListFilter): Promise<TransactionList> {
    const raw = await http.get<unknown>('admin/transactions', {
      query: toQuery(filter),
    });
    return transactionListSchema.parse(raw);
  },

  /** `DELETE admin/transactions/:id` — cancel a bonus or fine transaction. */
  async cancel(id: TransactionId): Promise<unknown> {
    return await http.delete<unknown>(`admin/transactions/${id}`);
  },

  /** Download one report as a file; the payload never carries paging. */
  async exportReport(
    kind: TransactionReportKind,
    payload: TransactionsReportPayload,
  ): Promise<Blob> {
    return await http.getBlob(REPORT_PATH[kind], { query: toQuery(payload) });
  },

  /** `GET admin/clients` — the client filter options (cpanel `getClientsList`). */
  async clientOptions(filter: OptionsFilter): Promise<OptionsPage<ClientOption>> {
    const raw = await http.get<unknown>('admin/clients', { query: toQuery(filter) });
    return parseOptionsPage(raw, (item) => clientOptionSchema.safeParse(item));
  },

  /** `GET admin/bonuses` — the bonus filter options (cpanel `getBonuseList`). */
  async bonusOptions(filter: OptionsFilter): Promise<OptionsPage<BonusOption>> {
    const raw = await http.get<unknown>('admin/bonuses', { query: toQuery(filter) });
    return parseOptionsPage(raw, (item) => bonusOptionSchema.safeParse(item));
  },

  /** `GET admin/fouls?is_rule=true` — the foul filter options (cpanel `getAllFouls`). */
  async foulOptions(filter: OptionsFilter): Promise<OptionsPage<FoulOption>> {
    const raw = await http.get<unknown>('admin/fouls', {
      query: toQuery({ ...filter, is_rule: true }),
    });
    return parseOptionsPage(raw, (item) => foulOptionSchema.safeParse(item));
  },
};
